/* MiniExplorerModal — pop-out card that appears when a viewer taps a
 * player row on the Live Community feed (or any other explorer surface).
 *
 * Shows purely game-facing info: nickname, rank (Scout → Master
 * Explorer), tier, species count, top Discovery Score, streak, badges,
 * and the three most recent species they've caught. Never leaks the
 * player's real legal name or email — those live server-side only.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Flame, Award, Sparkles, TrendingUp, Compass, Swords, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import RarityBadge from './RarityBadge';
import { useAuth } from '../contexts/AuthContext';

const TIER_META = {
  trailhead:  { label: 'Trailhead',  color: '#9CA3AF' },
  explorer:   { label: 'Explorer',   color: '#5FA9E5' },
  naturalist: { label: 'Naturalist', color: '#B27BC7' },
  pathfinder: { label: 'Pathfinder', color: '#E9C46A' },
  admin:      { label: 'Team',       color: '#E9C46A' },
};

// Pretty-print a raw badge slug like "explorer_begins" → "Explorer Begins"
function labelBadge(slug) {
  return String(slug || '').split('_').map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ');
}

export default function MiniExplorerModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [h2h, setH2h] = useState(null);
  const { user: me } = useAuth();

  useEffect(() => {
    if (!userId) return;
    let dead = false;
    api.get(`/explorers/${userId}/mini`)
      .then(r => { if (!dead) { r.data.found ? setData(r.data) : setError('Explorer not found'); } })
      .catch(() => { if (!dead) setError('Could not load Explorer'); });
    return () => { dead = true; };
  }, [userId]);

  // Head-to-head record vs this explorer — only fetched if the viewer
  // is signed in and looking at someone else's profile.
  useEffect(() => {
    if (!userId || !me?.user_id || me.user_id === userId) return;
    let dead = false;
    api.get(`/duels/head-to-head/${userId}`)
      .then(r => { if (!dead) setH2h(r.data); })
      .catch(() => {});
    return () => { dead = true; };
  }, [userId, me?.user_id]);

  // Progress to next rank — visual only, drives the little bar.
  const progress = (() => {
    if (!data?.next_rank_at) return { pct: 100, remaining: 0 };
    const current = data.species_count || 0;
    const target = data.next_rank_at;
    // Find the previous threshold by walking known ladder gaps.
    const LADDER = [0, 5, 15, 35, 75, 150, 300];
    let prev = 0;
    for (const t of LADDER) { if (t < target) prev = t; }
    const span = Math.max(1, target - prev);
    const done = Math.max(0, Math.min(span, current - prev));
    return { pct: Math.round((done / span) * 100), remaining: Math.max(0, target - current) };
  })();

  const tier = data ? (TIER_META[data.tier] || TIER_META.trailhead) : TIER_META.trailhead;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-3"
      data-testid="mini-explorer-modal"
    >
      <motion.div
        onClick={e => e.stopPropagation()}
        initial={{ y: 40, opacity: 0, scale: 0.96 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#0f1a15] overflow-hidden"
      >
        <button
          onClick={onClose}
          data-testid="mini-explorer-close"
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/50 hover:bg-black/70 border border-white/10"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {!data && !error && (
          <div className="p-10 flex flex-col items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Loading explorer…</p>
          </div>
        )}

        {error && (
          <div className="p-10 text-center">
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {data && (
          <>
            {/* Header — gradient banner with avatar + nickname */}
            <div className="relative p-5 pb-4 bg-gradient-to-br from-emerald-900/60 via-teal-900/40 to-transparent">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <img
                    src={data.picture || 'data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 40 40%22><rect fill=%22%23222%22 width=%2240%22 height=%2240%22/></svg>'}
                    alt=""
                    onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                    className="w-16 h-16 rounded-2xl object-cover border-2 border-white/15"
                    data-testid="mini-explorer-picture"
                  />
                  <span
                    className="absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold border-2 border-[#0f1a15]"
                    style={{ backgroundColor: tier.color + '33', color: tier.color, borderColor: '#0f1a15' }}
                  >
                    {tier.label}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/80">Explorer</p>
                  <p className="font-serif text-2xl leading-tight truncate" data-testid="mini-explorer-nickname">
                    {data.nickname}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                    <Compass className="w-3 h-3" strokeWidth={2} />
                    <span className="uppercase tracking-widest text-[10px]" data-testid="mini-explorer-rank">{data.rank}</span>
                  </p>
                </div>
              </div>

              {/* Rank progress bar */}
              {data.next_rank && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                    <span>{data.rank}</span>
                    <span>{data.next_rank}</span>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progress.pct}%` }}
                      transition={{ duration: 0.7, ease: 'easeOut' }}
                      className="h-full bg-gradient-to-r from-accent to-emerald-300"
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 tabular-nums">
                    {progress.remaining} species to <span className="text-white/80">{data.next_rank}</span>
                  </p>
                </div>
              )}
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 border-y border-white/5 divide-x divide-white/5">
              <Stat icon={<Sparkles className="w-3 h-3" />} label="Species" value={data.species_count} testId="stat-species" />
              <Stat icon={<TrendingUp className="w-3 h-3" />} label="Top Score" value={data.top_discovery_score} testId="stat-score" />
              <Stat icon={<Flame className="w-3 h-3" />} label="Streak" value={data.streak} testId="stat-streak" />
            </div>

            {/* Recent discoveries */}
            {data.recent?.length > 0 && (
              <div className="p-4 space-y-2 border-b border-white/5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Recent finds</p>
                <div className="space-y-1.5" data-testid="mini-explorer-recent">
                  {data.recent.map((s) => (
                    <div key={s.species_id} className="flex items-center gap-2">
                      <RarityBadge rarity={s.rarity} size="sm" showParticles={false} />
                      <p className="text-sm truncate">{s.common_name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Badges */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Badges</p>
                {data.badge_count > (data.badges?.length || 0) && (
                  <p className="text-[10px] text-muted-foreground font-mono tabular-nums">
                    +{data.badge_count - data.badges.length} more
                  </p>
                )}
              </div>
              {data.badges?.length > 0 ? (
                <div className="flex flex-wrap gap-1.5" data-testid="mini-explorer-badges">
                  {data.badges.map((b) => (
                    <span
                      key={b}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-accent/10 border border-accent/30 text-[10px] uppercase tracking-widest text-accent"
                    >
                      <Award className="w-3 h-3" strokeWidth={2} />
                      {labelBadge(b)}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No badges earned yet.</p>
              )}
            </div>

            {/* Head-to-head strip — only when viewer has any prior
                duel history against this explorer. */}
            {me?.user_id && me.user_id !== data.user_id && h2h && (h2h.battles > 0 || h2h.pending > 0) && (
              <div className="p-4 pt-0" data-testid="mini-explorer-h2h">
                <div className="rounded-xl border border-white/10 bg-gradient-to-br from-rose-500/5 to-amber-500/5 p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                      You vs {data.nickname}
                    </p>
                    {h2h.pending > 0 && (
                      <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-300">
                        {h2h.pending} pending
                      </span>
                    )}
                  </div>
                  <p className="font-serif text-2xl mt-1 tabular-nums">
                    <span className="text-emerald-300">{h2h.wins}</span>
                    <span className="text-white/40 mx-1">–</span>
                    <span className="text-rose-300">{h2h.losses}</span>
                  </p>
                  {h2h.last_winner_side && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Last battle: <span className={h2h.last_winner_side === 'you' ? 'text-emerald-300' : 'text-rose-300'}>
                        {h2h.last_winner_side === 'you' ? 'you won' : `${data.nickname} won`}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Rival button — appears only when the viewer is logged in
                and is not looking at themselves. Opens the ChallengePanel
                slide-in. */}
            {me?.user_id && me.user_id !== data.user_id && (
              <div className="p-4 pt-0">
                <button
                  type="button"
                  onClick={() => setChallengeOpen(true)}
                  data-testid="mini-explorer-rival-btn"
                  className="w-full h-11 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-black font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.98] transition"
                >
                  <Swords className="w-4 h-4" strokeWidth={3} />
                  Rival {data.nickname}
                </button>
                <p className="text-[10px] text-muted-foreground text-center mt-2">
                  Challenge to a Battle Arena duel
                </p>
              </div>
            )}
          </>
        )}

        <AnimatePresence>
          {challengeOpen && data && (
            <ChallengePanel
              opponent={data}
              onClose={() => setChallengeOpen(false)}
              onSent={() => { setChallengeOpen(false); onClose(); }}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}

function Stat({ icon, label, value, testId }) {
  return (
    <div className="py-3 px-2 text-center" data-testid={testId}>
      <div className="flex items-center justify-center gap-1 text-muted-foreground">
        {icon}
        <p className="text-[9px] uppercase tracking-widest">{label}</p>
      </div>
      <p className="font-serif text-xl mt-0.5 tabular-nums">{value ?? 0}</p>
    </div>
  );
}

/* ChallengePanel — inline card picker + trash-talk that fires
 * `POST /api/duels/challenge` against the opponent. Slides in over
 * the Mini Explorer card so context is preserved.
 *
 * Errors surfaced (all from the async duels backend):
 *   409 duel_already_pending → suggest inbox / offer view
 *   400 self-challenge / missing card
 *   404 opponent or card not found
 *   401 not logged in → redirect to /sign-in
 */
function ChallengePanel({ opponent, onClose, onSent }) {
  const nav = useNavigate();
  const [cards, setCards] = useState(null);       // null=loading, [] = empty
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/cards?limit=60')
      .then(r => {
        // Filter out cards that are listed on the marketplace — the
        // duels endpoint rejects those with a 400.
        const usable = (r.data || []).filter(c => !c.listed);
        setCards(usable);
        if (usable.length > 0) setSelected(usable[0].card_id);
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          toast.error('Sign in to challenge Explorers');
          nav('/');
        }
        setCards([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    if (!selected) return;
    setSending(true);
    try {
      const { data } = await api.post('/duels/challenge', {
        opponent_user_id: opponent.user_id,
        challenger_card_id: selected,
        message: message.trim() || null,
      });
      toast.success(`Challenge sent to ${opponent.nickname}!`, {
        description: 'They have 48 hours to accept. Track it in Battle → Duels.',
        action: { label: 'View', onClick: () => nav('/battle/duels') },
      });
      onSent?.(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.duel_id) {
        toast.error('You already have a pending challenge with this Explorer.', {
          action: { label: 'View', onClick: () => nav('/battle/duels') },
        });
      } else if (status === 401) {
        toast.error('Sign in to challenge Explorers');
        nav('/');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not send challenge');
      }
    } finally {
      setSending(false);
    }
  };

  const selectedCard = cards?.find(c => c.card_id === selected);

  return (
    <motion.div
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0 bg-[#0f1a15] flex flex-col"
      data-testid="challenge-panel"
    >
      {/* Header */}
      <div className="flex items-center gap-2 p-4 border-b border-white/5">
        <button
          type="button"
          onClick={onClose}
          data-testid="challenge-back"
          className="p-1.5 rounded-full hover:bg-white/5"
          aria-label="Back"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.25em] text-rose-300">Battle Arena · Challenge</p>
          <p className="font-serif text-lg leading-tight truncate">vs {opponent.nickname}</p>
        </div>
      </div>

      {/* Card picker */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          Pick your fighter
        </p>

        {cards === null && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your cards…
          </div>
        )}

        {cards?.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-card p-6 text-center space-y-2">
            <Swords className="w-6 h-6 text-muted-foreground mx-auto" />
            <p className="text-sm">You don't have any Battle Cards yet.</p>
            <p className="text-xs text-muted-foreground">
              Discover a species with a Discovery Score of 100+ to forge your first fighter.
            </p>
            <button
              type="button"
              onClick={() => nav('/discover')}
              className="mt-2 px-4 py-2 rounded-full bg-accent text-black text-[11px] uppercase tracking-widest font-semibold"
            >
              Go Discover
            </button>
          </div>
        )}

        {cards?.length > 0 && (
          <div className="grid grid-cols-2 gap-2" data-testid="challenge-card-grid">
            {cards.map(c => {
              const isSel = c.card_id === selected;
              return (
                <button
                  key={c.card_id}
                  type="button"
                  onClick={() => setSelected(c.card_id)}
                  data-testid={`challenge-card-${c.card_id}`}
                  className={`text-left rounded-xl border p-2.5 transition ${
                    isSel
                      ? 'border-rose-400 bg-rose-500/10 shadow-[0_0_0_1px_rgba(251,113,133,0.4)]'
                      : 'border-white/10 bg-card hover:border-white/30'
                  }`}
                >
                  <div className="flex items-center gap-1 mb-1">
                    <RarityBadge rarity={c.rarity || 'common'} size="sm" showParticles={false} />
                    <span className="text-[9px] uppercase tracking-widest text-muted-foreground">
                      {c.serial_label?.split('·')?.[0]?.trim() || `#${String(c.serial_number || 0).padStart(4, '0')}`}
                    </span>
                  </div>
                  <p className="text-xs font-medium truncate">{c.species_common_name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[9px] text-muted-foreground uppercase">Rating</span>
                    <span className="font-mono text-xs tabular-nums text-accent">{c.battle_rating || 0}</span>
                  </div>
                  <div className="flex items-center justify-between mt-0.5 text-[9px] text-muted-foreground font-mono tabular-nums">
                    <span>W {c.wins ?? 0}</span>
                    <span>L {c.losses ?? 0}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Trash-talk */}
        {cards?.length > 0 && (
          <div className="space-y-1 pt-2">
            <label className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Trash-talk (optional)
            </label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value.slice(0, 200))}
              placeholder="Say hi, throw shade, or leave blank…"
              rows={2}
              data-testid="challenge-message"
              className="w-full rounded-xl bg-black/50 border border-white/10 p-2.5 text-xs outline-none focus:border-accent/50 resize-none"
            />
            <p className="text-[9px] text-muted-foreground text-right font-mono tabular-nums">
              {message.length}/200
            </p>
          </div>
        )}
      </div>

      {/* Footer with send button */}
      {cards?.length > 0 && (
        <div className="p-4 border-t border-white/5 space-y-2">
          {selectedCard && (
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground text-center">
              Sending <span className="text-white/80">{selectedCard.species_common_name}</span> · Rating <span className="text-accent font-mono">{selectedCard.battle_rating}</span>
            </p>
          )}
          <button
            type="button"
            onClick={send}
            disabled={!selected || sending}
            data-testid="challenge-send"
            className="w-full h-11 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-black font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 active:scale-[0.98] transition"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Swords className="w-4 h-4" strokeWidth={3} />}
            {sending ? 'Sending…' : `Send Challenge`}
          </button>
          <p className="text-[9px] text-muted-foreground text-center">
            48-hour window · winner takes bragging rights + ranking points
          </p>
        </div>
      )}
    </motion.div>
  );
}
